import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Steps, Button, Flex } from 'antd';
import { useGetCargo } from 'api/cargos/use-get-cargo';
import type { TTripTaskForCargoReq } from 'api/swagger';
import { useCargoTaskStore } from 'shared/stores';
import { useSelectCargoStore } from 'shared/stores/use-select-cargos';
import { TripTaskType } from 'types/trips';
import { TaskForm } from './components/task-form';
import type { ICargoTasksCreationModalProps } from './types';
import { LOADING_TASK_TYPES, UNLOADING_TASK_TYPES, validateCargoTask, buildCargoTask } from './utils';

export const CargoTasksCreationModal: React.FC<ICargoTasksCreationModalProps> = (props) => {
  const { open, onCancel, onComplete, isPending = false } = props;
  const { t } = useTranslation('trip');
  const { setCargo, clearCargo } = useCargoTaskStore();
  const { cargosWithSelection } = useSelectCargoStore();
  const singleCargoId = cargosWithSelection.keys().next().value;
  const { data: cargo } = useGetCargo(singleCargoId);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [loadingTask, setLoadingTask] = useState<TTripTaskForCargoReq | null>(null);

  const isLoadingStep = currentIndex === 0;
  const { taskType, locationMode, coordinates, address, warehouseId, reset, initializeFromTask } = useCargoTaskStore();

  useEffect(() => {
    if (open && cargo) {
      reset();
      setCargo(cargo);
      setCurrentIndex(0);
      setLoadingTask(null);
    }
  }, [open, cargo, reset, setCargo]);

  useEffect(() => {
    if (!open) {
      clearCargo();
    }
  }, [open, clearCargo]);

  const currentTaskTypes = isLoadingStep ? LOADING_TASK_TYPES : UNLOADING_TASK_TYPES;

  const taskTypeOptions = currentTaskTypes.map((type) => ({ value: type, label: TripTaskType[type] }));

  const handleClose = () => {
    reset();
    setCurrentIndex(0);
    setLoadingTask(null);
    onCancel();
  };

  const handleNext = () => {
    const task = buildCargoTask({ taskType, locationMode, coordinates, address, warehouseId });

    if (isLoadingStep) {
      setLoadingTask(task);
      reset();
      setCurrentIndex(1);
    } else if (loadingTask) {
      onComplete([loadingTask, task]);
    }
  };

  const handleBack = () => {
    if (!loadingTask) return;
    setCurrentIndex(0);
    initializeFromTask(loadingTask);
  };

  const stepItems = [{ title: t('loading_task') }, { title: t('unloading_task') }];
  const isStepValid = validateCargoTask({ taskType, locationMode, coordinates, address, warehouseId });

  return (
    <Modal
      title={isLoadingStep ? t('create_loading_task') : t('create_unloading_task')}
      open={open}
      closable={!isPending}
      maskClosable={!isPending}
      width={720}
      footer={
        <Flex justify='space-between'>
          <Button disabled={isPending} onClick={handleClose}>
            {t('common:cancel')}
          </Button>
          <Flex gap='small'>
            {currentIndex === 1 && (
              <Button disabled={isPending} onClick={handleBack}>
                {t('common:back')}
              </Button>
            )}
            <Button type='primary' loading={isPending} disabled={!isStepValid || isPending} onClick={handleNext}>
              {isLoadingStep ? t('common:next') : t('common:save')}
            </Button>
          </Flex>
        </Flex>
      }
      onCancel={handleClose}>
      <Flex gap='large' vertical>
        <Steps current={currentIndex} items={stepItems} />
        <TaskForm taskTypeOptions={taskTypeOptions} />
      </Flex>
    </Modal>
  );
};
